import React, { useState, useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Search, BookOpen, MessageSquare, ChevronRight, Loader2, Send, FileText, Briefcase, CalendarDays, Plane, LogOut as ExitIcon, Sparkles, Pencil, Trash2, Plus } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import api from '@/lib/api';
import { toast } from 'sonner';
import { isAdmin, isHR } from '@/lib/auth';

const CATEGORY_META = {
  attendance: { label: 'Attendance', icon: CalendarDays, color: 'bg-blue-50 text-blue-700 border-blue-200' },
  leave: { label: 'Leave', icon: CalendarDays, color: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  travel: { label: 'Travel', icon: Plane, color: 'bg-amber-50 text-amber-700 border-amber-200' },
  general: { label: 'General', icon: Briefcase, color: 'bg-slate-50 text-slate-700 border-slate-200' },
  exit: { label: 'Exit Policy', icon: ExitIcon, color: 'bg-red-50 text-red-700 border-red-200' },
};

function CategoryBadge({ category }) {
  const meta = CATEGORY_META[category] || CATEGORY_META.general;
  return (
    <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full border ${meta.color}`}>
      {meta.label}
    </span>
  );
}

function SectionTOC({ sections, activeId, onSelect, filter }) {
  const grouped = {};
  const filtered = filter
    ? sections.filter(s => s.content && (s.title.toLowerCase().includes(filter.toLowerCase()) || s.category.toLowerCase().includes(filter.toLowerCase())))
    : sections.filter(s => s.content);
  filtered.forEach(s => {
    if (!grouped[s.category]) grouped[s.category] = [];
    grouped[s.category].push(s);
  });
  const order = ['attendance', 'leave', 'travel', 'general', 'exit'];
  return (
    <div className="space-y-4">
      {order.filter(c => grouped[c]).map(cat => (
        <div key={cat}>
          <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1.5 px-1">
            {CATEGORY_META[cat]?.label || cat}
          </p>
          <div className="space-y-0.5">
            {grouped[cat].map(sec => (
              <button
                key={sec.id}
                data-testid={`policy-toc-${sec.id}`}
                onClick={() => onSelect(sec)}
                className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors duration-100 ${
                  activeId === sec.id
                    ? 'bg-[#0D2850] text-white font-medium'
                    : 'text-slate-700 hover:bg-slate-100'
                }`}
              >
                <span className="flex items-center gap-1.5">
                  {sec.title}
                  {sec.updated_at && (Date.now() - new Date(sec.updated_at).getTime() < 7 * 86400 * 1000) && (
                    <span className="inline-block w-1.5 h-1.5 rounded-full bg-orange-500 shrink-0" title="Updated recently" />
                  )}
                </span>
              </button>
            ))}
          </div>
        </div>
      ))}
      {filtered.length === 0 && <p className="text-sm text-slate-400 text-center py-4">No sections found</p>}
    </div>
  );
}

function SectionContent({ section, admin, onEdit, onDelete }) {
  if (!section) return (
    <div className="flex flex-col items-center justify-center h-full text-slate-400 gap-3 py-20">
      <BookOpen className="w-12 h-12 opacity-40" />
      <p className="text-sm">Select a section from the table of contents</p>
    </div>
  );
  const isRecent = section.updated_at && (Date.now() - new Date(section.updated_at).getTime() < 7 * 86400 * 1000);
  return (
    <div data-testid="policy-section-content">
      <div className="flex items-center gap-3 mb-4">
        <CategoryBadge category={section.category} />
        <span className="text-xs text-slate-400">Pages {section.pages?.join(', ')}</span>
        {isRecent && <Badge className="bg-orange-100 text-orange-700 text-[10px] hover:bg-orange-100 animate-pulse">Updated</Badge>}
        {admin && (
          <div className="ml-auto flex gap-1.5">
            <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => onEdit(section)} data-testid="policy-edit-btn"><Pencil className="w-3 h-3 mr-1" />Edit</Button>
            <Button size="sm" variant="outline" className="h-7 text-xs border-red-200 text-red-600 hover:bg-red-50" onClick={() => onDelete(section)} data-testid="policy-delete-btn"><Trash2 className="w-3 h-3 mr-1" />Delete</Button>
          </div>
        )}
      </div>
      <h2 className="text-xl font-semibold text-[#0D2850] mb-4">{section.title}</h2>
      {section.updated_at && <p className="text-xs text-slate-400 mb-3">Last updated: {new Date(section.updated_at).toLocaleDateString()} by {section.updated_by || 'Admin'}</p>}
      <div className="prose prose-sm prose-slate max-w-none leading-relaxed whitespace-pre-wrap text-slate-700">
        {section.content}
      </div>
    </div>
  );
}

function SearchResults({ results, onSelect, query }) {
  if (!results) return null;
  if (results.length === 0) return (
    <div className="text-center py-10 text-slate-400">
      <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
      <p className="text-sm">No results found for "{query}"</p>
    </div>
  );
  return (
    <div className="space-y-3" data-testid="search-results">
      <p className="text-xs text-slate-500">{results.length} section{results.length !== 1 ? 's' : ''} found</p>
      {results.map(r => (
        <button
          key={r.id}
          onClick={() => onSelect(r)}
          data-testid={`search-result-${r.id}`}
          className="w-full text-left p-4 border border-slate-200 rounded-lg hover:border-[#0D2850]/30 hover:shadow-sm transition-all group"
        >
          <div className="flex items-center justify-between mb-1.5">
            <h4 className="font-medium text-sm text-[#0D2850] group-hover:underline">{r.title}</h4>
            <div className="flex items-center gap-2">
              <CategoryBadge category={r.category} />
              <span className="text-[10px] text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded">{r.match_count} match{r.match_count !== 1 ? 'es' : ''}</span>
            </div>
          </div>
          {r.snippets?.slice(0, 2).map((sn, i) => (
            <p key={i} className="text-xs text-slate-500 mt-1 line-clamp-2">{sn}</p>
          ))}
        </button>
      ))}
    </div>
  );
}

function AskAI() {
  const [question, setQuestion] = useState('');
  const [conversation, setConversation] = useState([]);
  const [loading, setLoading] = useState(false);
  const chatEnd = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    chatEnd.current?.scrollIntoView({ behavior: 'smooth' });
  }, [conversation]);

  const handleAsk = async () => {
    const q = question.trim();
    if (!q || loading) return;
    setConversation(prev => [...prev, { role: 'user', text: q }]);
    setQuestion('');
    setLoading(true);
    try {
      const res = await api.post('/api/hr-policy/ask', { question: q });
      setConversation(prev => [...prev, { role: 'ai', text: res.data.answer }]);
    } catch (e) {
      setConversation(prev => [...prev, { role: 'ai', text: 'Sorry, I could not process your question. Please try again.' }]);
      toast.error('Failed to get AI response');
    }
    setLoading(false);
    inputRef.current?.focus();
  };

  const suggestions = [
    "How many casual leaves am I entitled to?",
    "What is the notice period for resignation?",
    "What are the office timings?",
    "Explain the maternity leave policy",
    "What happens if I'm late to work?",
    "What is the travel allowance policy?",
  ];

  return (
    <div className="flex flex-col h-[calc(100vh-260px)] min-h-[400px]" data-testid="ask-ai-panel">
      <div className="flex-1 overflow-y-auto px-1 pb-4">
        {conversation.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center gap-4">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#0D2850] to-[#1a3d6e] flex items-center justify-center">
              <Sparkles className="w-7 h-7 text-[#F5CB08]" />
            </div>
            <div>
              <h3 className="font-semibold text-[#0D2850] mb-1">Ask about HR Policy</h3>
              <p className="text-xs text-slate-500 max-w-xs">Get instant answers from the company HR policy document using AI</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 w-full max-w-lg mt-2">
              {suggestions.map((s, i) => (
                <button
                  key={i}
                  data-testid={`ai-suggestion-${i}`}
                  onClick={() => { setQuestion(s); inputRef.current?.focus(); }}
                  className="text-left text-xs px-3 py-2.5 rounded-lg border border-slate-200 text-slate-600 hover:border-[#0D2850]/30 hover:bg-slate-50 transition-colors"
                >
                  {s}
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="space-y-4 pt-2">
            {conversation.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] px-4 py-3 rounded-2xl text-sm leading-relaxed ${
                  msg.role === 'user'
                    ? 'bg-[#0D2850] text-white rounded-br-md'
                    : 'bg-slate-100 text-slate-800 rounded-bl-md'
                }`}>
                  <div className="whitespace-pre-wrap">{msg.text}</div>
                </div>
              </div>
            ))}
            {loading && (
              <div className="flex justify-start">
                <div className="bg-slate-100 px-4 py-3 rounded-2xl rounded-bl-md flex items-center gap-2 text-slate-500 text-sm">
                  <Loader2 className="w-4 h-4 animate-spin" /> Thinking...
                </div>
              </div>
            )}
            <div ref={chatEnd} />
          </div>
        )}
      </div>
      <div className="border-t pt-3 flex gap-2">
        <Input
          ref={inputRef}
          data-testid="ai-question-input"
          value={question}
          onChange={e => setQuestion(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && handleAsk()}
          placeholder="Ask a question about HR policy..."
          className="flex-1"
          disabled={loading}
        />
        <Button
          data-testid="ai-ask-button"
          onClick={handleAsk}
          disabled={!question.trim() || loading}
          className="bg-[#0D2850] hover:bg-[#0B2246] px-4"
        >
          {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
        </Button>
      </div>
    </div>
  );
}

export default function HRPolicyPage() {
  const [sections, setSections] = useState([]);
  const [activeSection, setActiveSection] = useState(null);
  const [tocFilter, setTocFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editOpen, setEditOpen] = useState(false);
  const [editSection, setEditSection] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [editTitle, setEditTitle] = useState('');
  const [editCategory, setEditCategory] = useState('general');
  const [createOpen, setCreateOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ title: '', content: '', category: 'general' });
  const admin = isAdmin() || isHR();

  const loadSections = () => {
    api.get('/api/hr-policy/sections').then(r => {
      setSections(r.data);
      setLoading(false);
    }).catch(() => { toast.error('Failed to load policy'); setLoading(false); });
  };

  useEffect(() => { loadSections(); }, []);

  const handleSearch = async () => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) return;
    setSearching(true);
    try {
      const { data } = await api.get(`/api/hr-policy/search?q=${encodeURIComponent(searchQuery.trim())}`);
      setSearchResults(data);
    } catch (e) { toast.error('Search failed'); }
    setSearching(false);
  };

  const handleSearchResultClick = (result) => {
    const sec = sections.find(s => s.id === result.id);
    if (sec) {
      setActiveSection(sec);
      document.querySelector('[data-testid="policy-tab-browse"]')?.click();
    }
  };

  const openEdit = (sec) => {
    setEditSection(sec);
    setEditTitle(sec.title || '');
    setEditContent(sec.content || '');
    setEditCategory(sec.category || 'general');
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!editSection) return;
    try {
      await api.put(`/api/hr-policy/sections/${editSection.id}`, { title: editTitle, content: editContent, category: editCategory });
      toast.success('Policy section updated & announcement created');
      setEditOpen(false);
      loadSections();
    } catch (e) { toast.error('Failed to update'); }
  };

  const handleCreate = async () => {
    if (!createForm.title.trim() || !createForm.content.trim()) { toast.error('Title and content are required'); return; }
    try {
      await api.post('/api/hr-policy/sections', createForm);
      toast.success('New policy section created');
      setCreateOpen(false);
      setCreateForm({ title: '', content: '', category: 'general' });
      loadSections();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const handleDelete = async (sec) => {
    if (!window.confirm(`Are you sure you want to delete "${sec.title}"? This action cannot be undone.`)) return;
    try {
      await api.delete(`/api/hr-policy/sections/${sec.id}`);
      toast.success('Section deleted');
      if (activeSection?.id === sec.id) setActiveSection(null);
      loadSections();
    } catch (e) { toast.error('Failed to delete'); }
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <Loader2 className="w-6 h-6 animate-spin text-[#0D2850]" />
    </div>
  );

  return (
    <div data-testid="hr-policy-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="flex items-center gap-3 mb-2">
            <span className="text-[10px] font-bold tracking-widest uppercase bg-[#0D2850] text-white px-2.5 py-1 rounded">// HR POLICY</span>
          </div>
          <h1 className="text-2xl font-semibold text-[#0D2850] tracking-tight">Company Policy</h1>
          <p className="text-sm text-slate-500 mt-1">Browse, search, and ask questions about the Prismoline HR policy document</p>
        </div>
        {admin && (
          <Button className="bg-[#0D2850] hover:bg-[#0B2246] gap-1.5" onClick={() => { setCreateForm({ title: '', content: '', category: 'general' }); setCreateOpen(true); }} data-testid="policy-add-section-btn">
            <Plus className="w-4 h-4" /> Add Section
          </Button>
        )}
      </div>

      <Tabs defaultValue="browse" className="w-full">
        <TabsList className="bg-white border mb-4">
          <TabsTrigger value="browse" data-testid="policy-tab-browse" className="data-[state=active]:bg-[#0D2850] data-[state=active]:text-white gap-1.5">
            <BookOpen className="w-3.5 h-3.5" /> Browse
          </TabsTrigger>
          <TabsTrigger value="search" data-testid="policy-tab-search" className="data-[state=active]:bg-[#0D2850] data-[state=active]:text-white gap-1.5">
            <Search className="w-3.5 h-3.5" /> Search
          </TabsTrigger>
          <TabsTrigger value="ask" data-testid="policy-tab-ask" className="data-[state=active]:bg-[#0D2850] data-[state=active]:text-white gap-1.5">
            <Sparkles className="w-3.5 h-3.5" /> Ask AI
          </TabsTrigger>
        </TabsList>

        <TabsContent value="browse">
          <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
            {/* TOC Sidebar */}
            <div className="bg-white border rounded-lg p-3">
              <div className="relative mb-3">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
                <Input
                  data-testid="policy-toc-filter"
                  placeholder="Filter sections..."
                  value={tocFilter}
                  onChange={e => setTocFilter(e.target.value)}
                  className="pl-8 h-8 text-xs"
                />
              </div>
              <ScrollArea className="h-[calc(100vh-340px)] min-h-[300px]">
                <SectionTOC
                  sections={sections}
                  activeId={activeSection?.id}
                  onSelect={setActiveSection}
                  filter={tocFilter}
                />
              </ScrollArea>
            </div>
            {/* Content */}
            <div className="bg-white border rounded-lg p-6 min-h-[400px]">
              <ScrollArea className="h-[calc(100vh-300px)] min-h-[350px]">
                <SectionContent section={activeSection} admin={admin} onEdit={openEdit} onDelete={handleDelete} />
              </ScrollArea>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="search">
          <div className="bg-white border rounded-lg p-6">
            <div className="flex gap-2 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  data-testid="policy-search-input"
                  placeholder="Search the HR policy document..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
              <Button
                data-testid="policy-search-button"
                onClick={handleSearch}
                disabled={searching || searchQuery.trim().length < 2}
                className="bg-[#0D2850] hover:bg-[#0B2246]"
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
            </div>
            <SearchResults results={searchResults} onSelect={handleSearchResultClick} query={searchQuery} />
          </div>
        </TabsContent>

        <TabsContent value="ask">
          <div className="bg-white border rounded-lg p-6">
            <AskAI />
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Policy Section Dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Edit Policy Section</DialogTitle></DialogHeader>
          {editSection && (
            <div className="space-y-3 mt-2">
              <div>
                <Label className="text-xs">Title</Label>
                <Input value={editTitle} onChange={e => setEditTitle(e.target.value)} data-testid="policy-edit-title" />
              </div>
              <div>
                <Label className="text-xs">Category</Label>
                <Select value={editCategory} onValueChange={setEditCategory}>
                  <SelectTrigger data-testid="policy-edit-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general">General</SelectItem>
                    <SelectItem value="attendance">Attendance</SelectItem>
                    <SelectItem value="leave">Leave</SelectItem>
                    <SelectItem value="travel">Travel</SelectItem>
                    <SelectItem value="exit">Exit Policy</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Content</Label>
                <Textarea
                  value={editContent}
                  onChange={e => setEditContent(e.target.value)}
                  rows={15}
                  className="font-mono text-sm"
                  data-testid="policy-edit-content"
                />
              </div>
              <p className="text-xs text-slate-400">Saving will create an announcement notifying all employees of this update.</p>
              <Button onClick={handleSaveEdit} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="policy-edit-save">Save &amp; Announce</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create New Policy Section Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Add New Policy Section</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <Label className="text-xs">Title <span className="text-red-500">*</span></Label>
              <Input value={createForm.title} onChange={e => setCreateForm({...createForm, title: e.target.value})} placeholder="e.g. Remote Work Policy" data-testid="policy-create-title" />
            </div>
            <div>
              <Label className="text-xs">Category</Label>
              <Select value={createForm.category} onValueChange={v => setCreateForm({...createForm, category: v})}>
                <SelectTrigger data-testid="policy-create-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="general">General</SelectItem>
                  <SelectItem value="attendance">Attendance</SelectItem>
                  <SelectItem value="leave">Leave</SelectItem>
                  <SelectItem value="travel">Travel</SelectItem>
                  <SelectItem value="exit">Exit Policy</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Content <span className="text-red-500">*</span></Label>
              <Textarea
                value={createForm.content}
                onChange={e => setCreateForm({...createForm, content: e.target.value})}
                rows={15}
                className="font-mono text-sm"
                placeholder="Enter the full policy text..."
                data-testid="policy-create-content"
              />
            </div>
            <p className="text-xs text-slate-400">Creating a section will notify all employees via an announcement.</p>
            <Button onClick={handleCreate} className="w-full bg-[#0D2850] hover:bg-[#0B2246]" data-testid="policy-create-save">Create &amp; Announce</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
